<template>
  <NavBar pageName="Delivery Prices"></NavBar>
  <v-container style="margin-top: 185px;">
    <v-btn variant="text" size="small" @click="$router.push('/settings')" class="mb-2">
      <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
      Settings
    </v-btn>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ t.settings?.delivery_types || 'Delivery Types' }}</span>
        <div class="d-flex align-center ga-2">
          <v-switch
            v-model="showTerminated"
            :label="t.common?.show_terminated || 'Show terminated'"
            density="compact"
            hide-details
            color="primary"
            @update:model-value="loadDeliveryTypes"
          ></v-switch>
          <v-btn color="success" size="small" @click="openAddDialog">
            <v-icon class="mr-1">mdi-plus</v-icon>
            {{ t.common?.add || 'Add' }}
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="deliveryTypes"
          :loading="loading"
          :items-per-page="20"
          class="elevation-1"
        >
          <template v-slot:[`item.cost`]="{ item }">
            {{ item.cost }} {{ item.currency_code || 'EUR' }}
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              :color="item.status === 'active' ? 'success' : 'error'"
              size="small"
            >
              {{ item.status_name || item.status }}
            </v-chip>
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              icon
              size="small"
              variant="text"
              color="primary"
              @click="editDeliveryType(item)"
              :disabled="item.status === 'terminated'"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              icon
              size="small"
              variant="text"
              color="error"
              @click="confirmDelete(item)"
              :disabled="item.status === 'terminated'"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="showDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          {{ editingId ? (t.common?.edit || 'Edit') : (t.common?.add || 'Add') }} {{ t.settings?.delivery_type || 'Delivery Type' }}
        </v-card-title>

        <v-card-text>
          <v-alert v-if="editingId" type="info" density="compact" class="mb-4">
            {{ t.settings?.delivery_type_edit_warning || 'Editing will create a new version and terminate the current one. This preserves order history.' }}
          </v-alert>

          <v-text-field
            v-model="formData.name"
            :label="t.common?.name || 'Name'"
            variant="outlined"
            density="compact"
            :rules="[v => !!v || 'Name is required']"
          ></v-text-field>

          <v-row>
            <v-col cols="7">
              <v-text-field
                v-model.number="formData.cost"
                :label="t.common?.cost || 'Cost'"
                variant="outlined"
                density="compact"
                type="number"
                min="0"
                step="0.01"
                :rules="[v => v !== null && v !== '' || 'Cost is required']"
              ></v-text-field>
            </v-col>
            <v-col cols="5">
              <v-select
                v-model="formData.currency_id"
                :items="currencies"
                item-title="display"
                item-value="id"
                :label="t.common?.currency || 'Currency'"
                variant="outlined"
                density="compact"
              ></v-select>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">{{ t.common?.cancel || 'Cancel' }}</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="saveDeliveryType"
            :loading="saving"
            :disabled="!formData.name || formData.cost === null || formData.cost === ''"
          >
            {{ t.common?.save || 'Save' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>{{ t.common?.confirm || 'Confirm' }}</v-card-title>
        <v-card-text>
          {{ t.settings?.delivery_type_delete_confirm || 'Are you sure you want to terminate this delivery type? It will no longer be available for new orders.' }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">{{ t.common?.cancel || 'Cancel' }}</v-btn>
          <v-btn color="error" variant="flat" @click="deleteDeliveryType" :loading="deleting">
            {{ t.common?.delete || 'Delete' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import store from "@/store";
import { log } from "@/composables/useLog";
import { getDeliveryTypes, saveDeliveryType as saveDeliveryTypeAPI, deleteDeliveryType as deleteDeliveryTypeAPI } from "@/composables/useDeliveryTypesAPI";
import { t as tRef } from "@/composables/useLanguages";

export default defineComponent({
  name: 'DeliveryPricesSettings',
  components: {
    NavBar
  },
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      deliveryTypes: [],
      currencies: [],
      showTerminated: false,
      showDialog: false,
      showDeleteDialog: false,
      editingId: null,
      deleteId: null,
      formData: {
        name: '',
        cost: 0,
        currency_id: null
      },
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success'
    }
  },
  computed: {
    t() {
      return tRef.value;
    },
    token() {
      return store.getters.getToken;
    },
    headers() {
      return [
        { title: 'ID', key: 'id', width: '80px' },
        { title: this.t.common?.name || 'Name', key: 'name' },
        { title: this.t.common?.cost || 'Cost', key: 'cost', width: '120px' },
        { title: this.t.common?.status || 'Status', key: 'status', width: '120px' },
        { title: this.t.common?.created_at || 'Created', key: 'created_at', width: '150px' },
        { title: this.t.common?.actions || 'Actions', key: 'actions', sortable: false, width: '120px' }
      ];
    }
  },
  methods: {
    async loadDeliveryTypes() {
      this.loading = true;
      try {
        const response = await getDeliveryTypes(this.token, this.showTerminated);
        if (!response.error) {
          this.deliveryTypes = response.delivery_types || [];
          this.currencies = response.currencies || [];
          // Set default currency if not set
          if (this.currencies.length > 0 && !this.formData.currency_id) {
            const eurCurrency = this.currencies.find(c => c.code === 'EUR');
            this.formData.currency_id = eurCurrency?.id || this.currencies[0].id;
          }
        } else {
          this.showMessage(response.message || 'Error loading delivery types', 'error');
        }
      } catch (err) {
        log("Error loading delivery types:", err);
        this.showMessage('Error loading delivery types', 'error');
      } finally {
        this.loading = false;
      }
    },

    openAddDialog() {
      this.editingId = null;
      const eurCurrency = this.currencies.find(c => c.code === 'EUR');
      this.formData = { name: '', cost: 0, currency_id: eurCurrency?.id || this.currencies[0]?.id };
      this.showDialog = true;
    },

    editDeliveryType(item) {
      this.editingId = item.id;
      this.formData = {
        name: item.name,
        cost: item.cost,
        currency_id: item.currency_id
      };
      this.showDialog = true;
    },

    closeDialog() {
      this.showDialog = false;
      this.editingId = null;
      const eurCurrency = this.currencies.find(c => c.code === 'EUR');
      this.formData = { name: '', cost: 0, currency_id: eurCurrency?.id || this.currencies[0]?.id };
    },

    async saveDeliveryType() {
      this.saving = true;
      try {
        const data = {
          id: this.editingId,
          name: this.formData.name,
          cost: this.formData.cost,
          currency_id: this.formData.currency_id
        };

        const response = await saveDeliveryTypeAPI(this.token, data);
        if (!response.error) {
          this.showMessage(response.message || 'Delivery type saved', 'success');
          this.closeDialog();
          await this.loadDeliveryTypes();
        } else {
          this.showMessage(response.message || 'Error saving delivery type', 'error');
        }
      } catch (err) {
        log("Error saving delivery type:", err);
        this.showMessage('Error saving delivery type', 'error');
      } finally {
        this.saving = false;
      }
    },

    confirmDelete(item) {
      this.deleteId = item.id;
      this.showDeleteDialog = true;
    },

    async deleteDeliveryType() {
      this.deleting = true;
      try {
        const response = await deleteDeliveryTypeAPI(this.token, this.deleteId);
        if (!response.error) {
          this.showMessage(response.message || 'Delivery type terminated', 'success');
          this.showDeleteDialog = false;
          this.deleteId = null;
          await this.loadDeliveryTypes();
        } else {
          this.showMessage(response.message || 'Error deleting delivery type', 'error');
        }
      } catch (err) {
        log("Error deleting delivery type:", err);
        this.showMessage('Error deleting delivery type', 'error');
      } finally {
        this.deleting = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    showMessage(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.showSnackbar = true;
    }
  },
  mounted() {
    this.loadDeliveryTypes();
  }
});
</script>
